/**
 * @file    programsManagerModel.ts
 * @scope   CPR
 * @summary Control-state strategies, validation, tab descriptors and display helpers for the
 *          ProgramsManager view (contributor programs of the rollup metric), plus the adapters
 *          that let the Enterprise filter editors work on rollup data.
 * @consumes ControlState, RollupProgramModel, RollupMetricModel, FieldsManager models,
 *           filterValidation
 * @produces ProgramItemState, ProgramGlobalState, compute* strategies, ProgramValidationError,
 *           getProgramErrors, getProgramErrorMessage, isProgramIncomplete, hasDuplicatePrograms,
 *           isProgramSelectionIncomplete, isProgramInvalid, tab descriptors,
 *           ROLLUP_CALCULATION_TYPE_OPTIONS, getFilterCountText,
 *           toPartitionFiltersUserFields, toProgramFilterField
 * @sideEffects None (pure).
 * @notes Each program is persisted as a `QueryCollections` peer (`DataCollectionGuid` +
 *        `OriginId` only). Programs are shared by every calculation of the rollup.
 *        TODO(pending): per-program question/filters are drawer-only (see RollupProgramModel).
 */
import { ControlState } from '../../../../common/models/controlState';
import { Tab } from 'components/TabNavigator/TabNavigator';
import { RollupCalculationModel } from 'models/CrossProgramRollup/RollupCalculationModel';
import { CPR_AGGREGATION_TYPES } from '../../constants';
import {
  FieldFilter,
  SelectedFieldModel,
  SelectedUserFields,
} from 'models/Enterprise/FieldsManagerModel';
import { RollupMetricModel } from 'models/CrossProgramRollup/RollupMetricModel';
import {
  RollupProgramModel,
  isSameRollupProgram,
} from 'models/CrossProgramRollup/RollupProgramModel';
import { areFiltersInvalid } from '../../../Enterprise/utils/validation/filterValidation';

/** Program picker option (a workspace data collection). */
export interface ProgramOption {
  value: string;
  label: string;
  originId: string;
}

/** Question picker option (a field of the selected program). */
export interface QuestionOption {
  value: string;
  label: string;
}

export const ProgramsManagerTab: Tab = {
  name: 'CprProgramsManager',
  displayName: 'Manage Programs',
};

/** Distinct names from the Enterprise filter tabs: both route sets would otherwise render. */
export const CprPartitionFiltersTab: Tab = {
  name: 'CprPartitionFilters',
  displayName: 'Filters',
};

export const CprProgramFiltersTab: Tab = {
  name: 'CprProgramFilters',
  displayName: 'Filters',
};

/** TODO: replace with the GuardRails `CustomCalculations` catalogue for CrossProgramRollup. */
export const ROLLUP_CALCULATION_TYPE_OPTIONS = [
  { value: 'Percentage', label: 'Percentage' },
  { value: 'Mean', label: 'Mean' },
  { value: 'NPS', label: 'NPS' },
];

/** Calculation type → backend `CustomCalculations` item (`type` is camelCase on the wire). */
export const toRollupCalculations = (calculationType?: string): RollupCalculationModel[] => {
  const option = ROLLUP_CALCULATION_TYPE_OPTIONS.find((o) => o.value === calculationType);
  return option ? [{ Type: option.value.toLowerCase(), DisplayName: option.label }] : [];
};

/** Backend `RollupAggregationType` options (docs/guardrails.md, "AggregationType"). */
export const ROLLUP_AGGREGATION_TYPE_OPTIONS = CPR_AGGREGATION_TYPES.map((value) => ({
  value,
  label: value.replace(/([a-z])([A-Z])/g, '$1 $2'),
}));

export interface ProgramItemState {
  Drag: ControlState;
  Delete: ControlState;
}

export interface ProgramGlobalState {
  AddProgram: ControlState;
}

/** Per-row strategy: drag only makes sense with 2+ rows. */
export const computeProgramItemState = (
  _item: RollupProgramModel,
  _index: number,
  items: RollupProgramModel[]
): ProgramItemState => ({
  Drag: { visible: true, disabled: items.length <= 1 },
  Delete: { visible: true, disabled: false },
});

/** List-level strategy. */
export const computeProgramGlobalState = (): ProgramGlobalState => ({
  AddProgram: { visible: true, disabled: false },
});

/** Rules a program (or the list) can break. */
export type ProgramValidationError = 'incompleteProgram' | 'duplicateProgram';

const PROGRAM_ERROR_MESSAGES: Record<ProgramValidationError, string> = {
  incompleteProgram: 'The program is missing its data collection or origin.',
  duplicateProgram: 'This program is already part of the rollup.',
};

/** WHY: Hephaestus rejects contributors with an empty `DataCollectionGuid` / `OriginId`. */
export const isProgramIncomplete = (program: RollupProgramModel): boolean =>
  !program.dataCollectionGuid || !program.originId;

export const hasDuplicatePrograms = (programs: RollupProgramModel[]): boolean =>
  programs.some((p, i) => programs.findIndex((q) => isSameRollupProgram(p, q)) !== i);

/** @returns Every rule the program at `index` breaks; empty = valid. */
export const getProgramErrors = (
  programs: RollupProgramModel[],
  index: number
): ProgramValidationError[] => {
  const program = programs[index];
  const errors: ProgramValidationError[] = [];
  if (isProgramIncomplete(program)) errors.push('incompleteProgram');
  if (programs.findIndex((q) => isSameRollupProgram(program, q)) !== index) {
    errors.push('duplicateProgram');
  }
  return errors;
};

/** @returns The tooltip text for the given errors, one per line. */
export const getProgramErrorMessage = (errors: ProgramValidationError[]): string =>
  errors.map((error) => PROGRAM_ERROR_MESSAGES[error]).join('\n');

/** UI gate: persisted identity incomplete OR (pending, drawer-only) question not picked. */
export const isProgramSelectionIncomplete = (program: RollupProgramModel): boolean =>
  isProgramIncomplete(program) || !program.questionGuid;

/** Card-level validity: incomplete selection OR incomplete filters, once the card is touched. */
export const isProgramInvalid = (program: RollupProgramModel, isTouched: boolean): boolean =>
  (isTouched && isProgramSelectionIncomplete(program)) ||
  areFiltersInvalid(program.filters ?? [], isTouched);

/** Same wording as the FieldsManager partition filters counter. */
export const getFilterCountText = (filters: FieldFilter[] | undefined): string => {
  const total = filters?.length ?? 0;
  if (total === 0) return 'No filters';
  return total === 1 ? '1 filter' : `${total} filters`;
};

/** Wraps the rollup's partition filters in the shape PartitionFiltersManager expects. */
export const toPartitionFiltersUserFields = (metric: RollupMetricModel): SelectedUserFields => ({
  partitionGuid: metric.id,
  fields: [],
  partitionFilters: metric.partitionFilters ?? [],
});

/** Wraps a program in the shape FiltersSettingsManager expects. */
export const toProgramFilterField = (
  program: RollupProgramModel,
  index: number
): SelectedFieldModel => ({
  fieldNumber: index + 1,
  field: {
    fieldGuid: program.questionGuid ?? '',
    fieldName: program.questionLabel ?? '',
    displayName: program.displayName,
  },
  displayName: program.displayName ?? `Program ${index + 1}`,
  calculationType: null,
  precision: '',
  normalize: false,
  statTesting: false,
  filters: program.filters ?? [],
});
